/**
 * Tag-driven featured product swaps for the collection grid.
 *
 * The Liquid in sections/main-collection.liquid still applies the original
 * position-based featured classes (`product-grid__item--featured`,
 * `--featured-left`, `--featured-right`) so the big-card layout is always
 * present. This script only SWAPS tagged products into those existing slots:
 *
 *   - Products tagged "featured 1", "featured 2", ... go into the Nth slot.
 *   - Products tagged plain "featured" act as a fallback pool: if no card
 *     tagged "featured N" is currently rendered (e.g. it's on a later
 *     infinite-scroll page or filtered out), the next unused fallback card
 *     fills that slot.
 *   - Slots with neither an exact match nor an available fallback keep their
 *     original occupant.
 *
 * The observer re-runs whenever Horizon's infinite scroll mutates the grid
 * (assets/paginated-list.js prepends or appends new cards).
 */

const FEATURED_CLASS = 'product-grid__item--featured';
const FEATURED_LEFT_CLASS = 'product-grid__item--featured-left';
const FEATURED_RIGHT_CLASS = 'product-grid__item--featured-right';

class HorizonFeaturedGrid {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[ref="grid"]');
    this.observer = null;
    this.isComposing = false;

    if (!this.grid) return;

    this.compose();
    this.observe();
  }

  observe() {
    this.observer = new MutationObserver(() => {
      if (this.isComposing) return;
      this.compose();
    });

    this.observer.observe(this.grid, { childList: true });
  }

  getCards() {
    return Array.from(this.grid.querySelectorAll(':scope > [ref="cards[]"]'));
  }

  compose() {
    if (!this.grid) return;

    const cards = this.getCards();
    if (cards.length === 0) return;

    // Slots in DOM order (the Liquid already applied featured classes here).
    const slots = cards.filter((card) => card.classList.contains(FEATURED_CLASS));
    if (slots.length === 0) return;

    // Exact picks: "featured N". First occurrence of each N wins.
    const seenOrders = new Set();
    const orderedByNum = new Map();
    cards.forEach((card) => {
      const order = Number(card.dataset.featuredOrder || 0);
      if (!order || order < 1) return;
      if (seenOrders.has(order)) return;
      seenOrders.add(order);
      orderedByNum.set(order, card);
    });

    // Fallback pool: plain "featured"-tagged products in DOM order.
    // Exclude any card that already has an exact featured-order assignment.
    const exactCards = new Set(orderedByNum.values());
    const fallbackPool = cards.filter(
      (card) => card.dataset.featuredFallback === 'true' && !exactCards.has(card)
    );

    // Per-slot assignment: exact match -> fallback -> leave existing occupant.
    const assignments = [];
    let fallbackIndex = 0;
    for (let i = 0; i < slots.length; i += 1) {
      const exact = orderedByNum.get(i + 1);
      if (exact) {
        assignments.push(exact);
        continue;
      }
      const fallback = fallbackPool[fallbackIndex];
      if (fallback) {
        fallbackIndex += 1;
        assignments.push(fallback);
        continue;
      }
      assignments.push(null);
    }

    if (assignments.every((a) => a === null)) return;

    this.isComposing = true;

    assignments.forEach((taggedCard, index) => {
      if (!taggedCard) return;
      const slot = slots[index];
      if (!slot || slot === taggedCard) return;

      // Capture which featured variant the slot was using.
      const isLeft = slot.classList.contains(FEATURED_LEFT_CLASS);
      const isRight = slot.classList.contains(FEATURED_RIGHT_CLASS);

      // Swap the two elements' DOM positions.
      swapNodes(slot, taggedCard);

      // Transfer featured classes from the old slot occupant to the tagged card.
      slot.classList.remove(FEATURED_CLASS, FEATURED_LEFT_CLASS, FEATURED_RIGHT_CLASS);
      taggedCard.classList.add(FEATURED_CLASS);
      if (isLeft) taggedCard.classList.add(FEATURED_LEFT_CLASS);
      if (isRight) taggedCard.classList.add(FEATURED_RIGHT_CLASS);
    });

    queueMicrotask(() => {
      this.isComposing = false;
    });
  }
}

/**
 * Swap two sibling-or-not nodes in the DOM by exchanging their positions.
 */
function swapNodes(a, b) {
  const aParent = a.parentNode;
  const bParent = b.parentNode;
  if (!aParent || !bParent) return;

  const aNext = a.nextSibling === b ? a : a.nextSibling;
  bParent.insertBefore(a, b);
  aParent.insertBefore(b, aNext);
}

function mountFeaturedGrids(scope = document) {
  const roots = scope.querySelectorAll
    ? scope.querySelectorAll('results-list')
    : [];
  roots.forEach((root) => {
    if (root.__horizonFeaturedGridMounted) return;
    root.__horizonFeaturedGridMounted = true;
    new HorizonFeaturedGrid(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountFeaturedGrids(), { once: true });
} else {
  mountFeaturedGrids();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    mountFeaturedGrids(event.target);
  }
});
